import { Button, Modal } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { gql, useMutation } from "@apollo/client";
import swal from "sweetalert";
import {
  useAddAssignmentToClusterMutation,
  useGetAllAssignmentsQuery,
  useGetAssignmentByClusterQuery,
  useGetStudentsByClusterLazyQuery,
} from "../../../../graphql/generated.tsx";
import AdminLayout from "../../Layout/index.jsx";
import Select from "react-select";
import AssignmentCard from "../AssignmentCard.jsx";
import { toast } from "../../../../toast";

const REVOKE_ASSIGNMENT = gql`
  mutation RevokeAssignment($assignmentId: String!, $studentIds: [String!]!, $campus_name: String!) {
    revokeAssignment(
      assignmentId: $assignmentId
      studentIds: $studentIds
      campus_name: $campus_name
    ) {
      Status
      revokedCount
    }
  }
`;

const REQUEST_DELETION = gql`
  mutation RequestDeletion(
    $typeOfDeletion: String!
    $payload: JSON!
    $reasonForDeletion: String!
    $requestedBy: String!
    $title: String!
  ) {
    requestDeletion(
      typeOfDeletion: $typeOfDeletion
      payload: $payload
      reasonForDeletion: $reasonForDeletion
      requestedBy: $requestedBy
      title: $title
    ) {
      id
      statusOfApproval
    }
  }
`;

const ManageAssignmentTab = ({ campusName, clusterId, ClusterData }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignments, setAssignments] = useState([]);

  // Revoke modal state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeSelectedAssignment, setRevokeSelectedAssignment] = useState(null);
  const [pastedEmails, setPastedEmails] = useState("");
  const [isRevoking, setIsRevoking] = useState(false);
  const [isAddingAssignment, setIsAddingAssignment] = useState(false);

  const { data: allAssignmentData } = useGetAllAssignmentsQuery();
  const [addAssignmentToCluster] = useAddAssignmentToClusterMutation();
  const [revokeAssignmentMutation] = useMutation(REVOKE_ASSIGNMENT);
  const [requestDeletion] = useMutation(REQUEST_DELETION);
  const [fetchClusterStudents] = useGetStudentsByClusterLazyQuery();

  const {
    data: assignmentData,
    error: assignmentError,
    refetch,
  } = useGetAssignmentByClusterQuery({
    variables: { campusName, clusterId },
    onCompleted: (data) => setAssignments(data.getAssignmentByCluster),
  });

  const handleAddAssignment = async () => {
    if (isAddingAssignment) return;
    if (!selectedAssignment?.value) {
      toast.warning("Please select an assignment.", { title: "Missing Selection" });
      return;
    }
    try {
      setIsAddingAssignment(true);
      const isEmail = ClusterData?.getCluster?.isAssessmentMail ?? false;
      await addAssignmentToCluster({
        variables: {
          clusterId,
          assignmentId: selectedAssignment?.value,
          campusName,
          isEmail,
        },
      });
      toast.success("Assignment Assigned Successfully", { title: "Assignment Assigned" });
      setSelectedAssignment(null);
      refetch();
      setShowModal(false);
    } catch (error) {
      console.error("Error adding assignment:", error);
      toast.error("Failed to assign assignment", { title: "Assignment Failed" });
    } finally {
      setIsAddingAssignment(false);
    }
  };

  useEffect(() => {
    refetch();
    setAssignments(assignmentData?.getAssignmentByCluster);
  }, [assignmentData]);

  const handleRemoveAssignment = async (assignmentId) => {
    const assignment = assignments?.find((a) => a.id === assignmentId);
    const assignmentName = assignment?.name || "Assignment";

    const reason = await swal({
      text: `Enter reason for requesting removal of assignment "${assignmentName}" from this cluster:`,
      content: "input",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Submit",
          closeModal: false,
        },
      },
    });

    if (!reason) {
      swal.close();
      return;
    }

    const payload = {
      operationName: "removeAssignmentFromCluster",
      variables: {
        campusName,
        clusterId,
        assignmentId,
      },
      query: `
        mutation removeAssignmentFromCluster($campusName: String!, $clusterId: String!, $assignmentId: String!) {
          removeAssignmentFromCluster(campusName: $campusName, clusterId: $clusterId, assignmentId: $assignmentId) {
            courseIds
            studentCount
          }
        }
      `,
    };

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const name = localStorage.getItem("userdisplayName");
    const requestedBy =
      name ||
      user?.displayName ||
      user?.userdisplayName ||
      user?.email ||
      "Unknown User";

    try {
      await requestDeletion({
        variables: {
          typeOfDeletion: "CLUSTER_ASSIGNMENT_REMOVE",
          payload,
          reasonForDeletion: reason,
          requestedBy,
          title: `Remove assignment "${assignmentName}" from cluster`,
        },
      });
      swal.close();
      toast.success("Removal request sent for approval", { title: "Request Sent" });
    } catch (error) {
      console.error("Error removing assignment:", error);
      toast.error("Failed to send removal request", { title: "Request Failed" });
    }
  };

  const handleBulkRevoke = async () => {
    if (!revokeSelectedAssignment) {
      toast.warning("Please select an assignment.", { title: "Missing Selection" });
      return;
    }

    let studentIds = [];

    const emails = pastedEmails
      .split(/[\n,]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      toast.warning("Please enter at least one email.", { title: "No Emails" });
      return;
    }
    const result = await fetchClusterStudents({ variables: { clusterId, limit: 1000, searchTerm: "" } });
    const allStudents = result.data?.getStudentsByCluster || [];
    studentIds = allStudents
      .filter((s) => emails.includes(s.email))
      .map((s) => s.id);
    if (studentIds.length === 0) {
      toast.error("No matching students found for the provided emails.", { title: "No Matches" });
      return;
    }

    try {
      setIsRevoking(true);
      const { data } = await revokeAssignmentMutation({
        variables: {
          assignmentId: revokeSelectedAssignment.value,
          studentIds,
          campus_name: campusName,
        },
      });
      toast.success(
        `Assignment revoked successfully for ${data.revokeAssignment.revokedCount ?? studentIds.length} student(s).`,
        { title: "Revoked" }
      );
      setShowRevokeModal(false);
      setRevokeSelectedAssignment(null);
      setPastedEmails("");
    } catch (err) {
      toast.error("Error revoking: " + err.message, { title: "Revoke Failed" });
    } finally {
      setIsRevoking(false);
    }
  };

  if (assignmentError)
    return <AdminLayout>Error: {assignmentError.message}</AdminLayout>;

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleRevokeModal = () => {
    setShowRevokeModal((v) => !v);
    setRevokeSelectedAssignment(null);
    setPastedEmails("");
  };

  return (
    <>
      <div className="p-10">
        <div className="flex justify-end items-end mb-8 gap-3 max-md:gap-2">
          <Button
            onClick={toggleRevokeModal}
            className="text-white p-0 rounded-lg shadow-md flex items-center bg-primary"
          >
            <span>Bulk Revoke</span>
          </Button>
          <Button
            onClick={toggleModal}
            className="text-white p-0 rounded-lg shadow-md flex items-center bg-primary"
          >
            <IoMdAdd className="mr-1 mt-1" /> <span>Add Assignment</span>
          </Button>
        </div>

        {/* ✅ Show message if no assignments are found */}
        {(!assignments || assignments.length === 0) ? (
          <div className="flex justify-center items-center h-40 text-gray-500 text-lg font-medium">
            No Assignments Found
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 max-md:p-5">
            {assignments.map((assignment, index) => (
              <AssignmentCard
                key={assignment.id}
                assignment_id={assignment.id}
                name={assignment.name}
                description={assignment.short_description}
                due_date={assignment.due_date}
                duration={assignment.duration}
                domain={assignment.domain}
                globalIndex={index}
                onRemove={() => handleRemoveAssignment(assignment.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal for Adding Assignment */}
      {/* Modal for Bulk Revoke */}
      <Modal show={showRevokeModal} onClose={toggleRevokeModal}>
        <Modal.Header>Bulk Revoke Assignment</Modal.Header>
        <Modal.Body>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleBulkRevoke();
            }}
          >
            {/* Step 1: Pick assignment (only already-assigned ones) */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Select Assignment
              </label>
              <Select
                options={(assignments || []).map((a) => ({
                  label: a.name,
                  value: a.id,
                }))}
                value={revokeSelectedAssignment}
                onChange={setRevokeSelectedAssignment}
                placeholder="Select an assigned assignment"
                isClearable
              />
            </div>

            {/* Step 2: Paste emails */}
            {revokeSelectedAssignment && (
              <div className="mb-4 h-[200px]">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Student Emails
                  <span className="font-normal text-gray-500 ml-1">(comma or newline separated)</span>
                </label>
                <textarea
                  rows={5}
                  value={pastedEmails}
                  onChange={(e) => setPastedEmails(e.target.value)}
                  placeholder={"student1@example.com, student2@example.com\nstudent3@example.com"}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0F537E] focus:border-[#0F537E] resize-none"
                />
              </div>
            )}

            <div className="flex items-center justify-between mt-[7.5rem]">
              <Button
                type="submit"
                disabled={isRevoking}
                className="bg-primary text-white"
              >
                {isRevoking ? "Revoking..." : "Revoke"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={showModal} onClose={isAddingAssignment ? undefined : toggleModal}>
        <Modal.Header>Add Assignment</Modal.Header>
        <Modal.Body>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddAssignment();
            }}
          >
            <div className="mb-4 h-[200px]">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Select Assignment
              </label>
              <Select
                options={allAssignmentData?.getAllAssignments?.map(
                  (assignment) => ({
                    label: assignment.name,
                    value: assignment.id,
                  })
                )}
                value={selectedAssignment}
                onChange={setSelectedAssignment}
                placeholder="Select an assignment"
                isClearable
              />
            </div>
            <div className="flex items-center justify-between">
              <Button
                type="submit"
                disabled={isAddingAssignment}
                className={isAddingAssignment ? "opacity-70 cursor-not-allowed" : ""}
              >
                {isAddingAssignment ? "Adding..." : "Add"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ManageAssignmentTab;
